import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft, Loader2, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ChoiceCard } from '@/components/ui/choice-card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { PaymentPreference } from '@/lib/draft-order-client';
import { ordersClient, type Order, type SwitchPaymentMethodResult } from '@/lib/orders-client';
import { PaymentHoldNotice } from './payment-hold-notice';
import type { OrderPaymentNotice } from './use-order-payment-actions';

interface SwitchPaymentMethodDialogProps {
  /** The unpaid order being switched, or null when the dialog is closed. */
  order: Order | null;
  /** The last hold refusal, shown in the dialog when it is about this order. */
  notice: OrderPaymentNotice | null;
  /** Resolves null when the switch was refused — the dialog then stays open for another try. */
  onSwitch: (order: Order, paymentPreference: PaymentPreference) => Promise<SwitchPaymentMethodResult | null>;
  onSwitched: (result: SwitchPaymentMethodResult) => void;
  onDismiss: () => void;
}

/** "Le pago en efectivo": moves an order awaiting its online payment to another method. */
export function SwitchPaymentMethodDialog({
  order,
  notice,
  onSwitch,
  onSwitched,
  onDismiss,
}: SwitchPaymentMethodDialogProps) {
  const { t } = useTranslation('orders');
  const { t: tc } = useTranslation('conversations');
  const [selected, setSelected] = useState<PaymentPreference | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const orderId = order?.id;
  useEffect(() => {
    setSelected(null);
  }, [orderId]);

  /**
   * Only `GET /orders/:id` carries `switchablePaymentMethods`, and the order handed in may come from a
   * list (the orders page, the customer profile) — so read it again on open, and again whenever the
   * surface's copy changes. `order: null` = the read failed: the handed-in copy's list stands when it
   * has one, and a list-only copy (which never has one) asks for a retry instead.
   */
  const [fresh, setFresh] = useState<{ orderId: string; order: Order | null } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const orderUpdatedAt = order?.updatedAt;
  useEffect(() => {
    if (!orderId) {
      setFresh(null);
      return;
    }
    let cancelled = false;
    ordersClient
      .getById(orderId)
      .then((response) => {
        if (!cancelled) setFresh({ orderId, order: response.data });
      })
      .catch((error: unknown) => {
        console.error('Failed to re-read the order before switching its payment method:', error);
        if (!cancelled) setFresh({ orderId, order: null });
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, orderUpdatedAt, attempt]);
  const current = fresh && fresh.orderId === orderId ? fresh : null;
  const loading = Boolean(orderId) && !current;
  const source = current?.order ?? order;
  // A failed read with no list to fall back on: "no alternatives" would be a guess, so say it failed.
  const loadFailed = Boolean(current) && !current?.order && source?.switchablePaymentMethods === undefined;

  const retry = () => {
    setFresh(null);
    setAttempt((previous) => previous + 1);
  };

  // Exactly what the server says this order can take — it already left out online card, the current
  // method, and anything the brand does not offer for the order's fulfilment type.
  const methods = source?.switchablePaymentMethods ?? [];
  const hasAlternatives = !loading && !loadFailed && methods.length > 0;
  // A re-read of the order can narrow the list while the dialog is open: never submit a dropped pick.
  const choice = selected && methods.includes(selected) ? selected : null;
  const holdNotice = notice && notice.orderId === orderId ? notice : null;

  const submit = async () => {
    if (!order || !choice) return;
    setSubmitting(true);
    try {
      const result = await onSwitch(order, choice);
      if (result) onSwitched(result);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={Boolean(order)} onOpenChange={(open) => !open && !submitting && onDismiss()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('switchPayment.title')}</DialogTitle>
          <DialogDescription>{t('switchPayment.description', { id: order?.id.slice(0, 8) ?? '' })}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {loading ? (
            <p role="status" className="flex items-center gap-2 px-1 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('switchPayment.loading')}
            </p>
          ) : loadFailed ? (
            <div
              role="alert"
              className="flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              <span>{t('switchPayment.loadFailed')}</span>
              <Button type="button" variant="outline" size="sm" onClick={retry}>
                <RotateCw className="h-3.5 w-3.5" />
                {t('switchPayment.retry')}
              </Button>
            </div>
          ) : hasAlternatives ? (
            <>
              <div role="radiogroup" aria-label={t('switchPayment.methodsLabel')} className="grid gap-1.5">
                {methods.map((method) => (
                  <ChoiceCard
                    key={method}
                    className="p-3"
                    selected={choice === method}
                    onSelect={() => setSelected(method)}
                    disabled={submitting}
                    title={tc(`cart.payment.methods.${method}`)}
                  />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">{t('switchPayment.hint')}</p>
            </>
          ) : (
            <p className="rounded-lg border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
              {t('switchPayment.noAlternatives')}
            </p>
          )}
          {holdNotice && <PaymentHoldNotice code={holdNotice.code} />}
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onDismiss} disabled={submitting}>
            {t('switchPayment.keep')}
          </Button>
          {hasAlternatives && (
            <Button type="button" loading={submitting} disabled={!choice} onClick={() => void submit()}>
              {!submitting && <ArrowRightLeft className="h-3.5 w-3.5" />}
              {t('switchPayment.confirm')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
